import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Menu } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Logo } from "@/components/site/Logo";
import { Btn } from "@/components/site/kit";
import { cn } from "@/lib/utils";
import { useMenu } from "@/context/SiteContext";

export const NAV = [
  { to: "/", label: "Home" },
  { to: "/products", label: "Products" },
  { to: "/services", label: "Services" },
  { to: "/pricing", label: "Pricing" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

const linkCls = ({ isActive }) =>
  cn("rounded-full px-4 py-2 text-sm font-semibold transition-colors", isActive ? "bg-[#EEF6FC] text-ocean" : "text-navy hover:text-ocean");

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const menu = useMenu("nav");

  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header className={cn("sticky top-0 z-40 border-b bg-white/95 backdrop-blur-md transition-shadow", scrolled ? "border-line shadow-soft" : "border-transparent")} data-testid="site-navbar">
      <nav className="container-x flex h-[72px] items-center justify-between gap-4" aria-label="Main">
        <Logo />
        <div className="hidden items-center gap-1 lg:flex">
          {menu.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === "/"} className={linkCls} data-testid={`nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}>{n.label}</NavLink>
          ))}
        </div>
        <div className="hidden lg:block">
          <Btn to="/contact#quote" className="px-6 py-3" testId="nav-get-quote-btn">Get a Free Quote</Btn>
        </div>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button className="grid h-11 w-11 place-items-center rounded-xl border border-line text-navy lg:hidden" aria-label="Open menu" data-testid="mobile-menu-btn">
              <Menu className="h-5 w-5" />
            </button>
          </SheetTrigger>
          <SheetContent side="right" className="w-[300px] bg-white">
            <SheetTitle className="sr-only">Menu</SheetTitle>
            <div className="mt-8 flex flex-col gap-1">
              {menu.map((n) => (
                <NavLink key={n.to} to={n.to} end={n.to === "/"} className={linkCls} data-testid={`mobile-nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}>{n.label}</NavLink>
              ))}
              <Btn to="/contact#quote" className="mt-6" testId="mobile-get-quote-btn">Get a Free Quote</Btn>
            </div>
          </SheetContent>
        </Sheet>
      </nav>
    </header>
  );
};
